/**
 * ChainSession — per-player-load session identity for the recommendation chain.
 *
 * Holds the in-memory watched `history` (the source of truth for the /chain
 * exclude-list + session vector) and an opaque `sessionId` used ONLY to group
 * success-metric events. Nothing is persisted: no cookie, no
 * localStorage/sessionStorage, no IP — session-scoped only.
 *
 * The chain engine is stateless and already excludes watched items by id and
 * title, so this module's only job is to supply an accurate watched list via
 * toChainParams(); it performs no network I/O.
 *
 * Usage:
 *   chainSession.recordWatched(idexperience)  // on VIDEO_ENDED / chosen-branch start
 *   chainSession.toChainParams()              // spread into the /chain request (read-only)
 *   chainSession.getSessionId(), .depth()     // for chain metrics
 */

import { generateRandomId } from "@/modules/randomId";
import { readMcpScope } from "@/modules/mcpScope";

function generateSessionId() {
  return generateRandomId();
}

export interface ChainParams {
  history: number[];
  exclude_ids: number[];
  publication_scope?: number[];
}

const sessionId = generateSessionId();
const watched: number[] = [];
// id -> title for watched experiences, so the assistant can be told what the
// viewer watched EARLIER this session by name (buildTurnContext), not just id
// — "recommend something similar to what I just watched" needs the title.
const titleById: Record<number, string> = {};

export function getSessionId() {
  return sessionId;
}

/**
 * Append a watched experience id (and, when known, its title). De-dupes when
 * the same id was JUST recorded (one experience fires VIDEO_ENDED once per
 * contained video), so history reflects distinct steps. Ignores null/NaN.
 */
export function recordWatched(idexperience: unknown, title?: unknown) {
  if (idexperience == null) {
    return;
  }
  const id = Number(idexperience);
  if (Number.isNaN(id)) {
    return;
  }
  if (title) {
    titleById[id] = String(title);
  }
  if (watched[watched.length - 1] === id) {
    return;
  }
  watched.push(id);
}

/**
 * Recently watched experiences as {id, title} pairs, most-recent-first,
 * unique by id, capped at `limit` (default 12). Only ids we have a title for
 * are included. For the assistant's per-turn context: the id lets Pixie fetch
 * a past recipe's detail on demand, the title names it in the prompt.
 */
export function recentWatched(limit?: number): { id: number; title: string }[] {
  const cap = typeof limit === "number" && limit > 0 ? limit : 12;
  const out: { id: number; title: string }[] = [];
  const seen: Record<number, boolean> = {};
  for (let i = watched.length - 1; i >= 0 && out.length < cap; i--) {
    const id = watched[i];
    const title = titleById[id];
    if (title && !seen[id]) {
      seen[id] = true;
      out.push({ id: id, title: title });
    }
  }
  return out;
}

/**
 * Read-only snapshot for a /chain request. Returns FRESH arrays so a caller
 * (e.g. a prefetch) can't mutate internal state.
 *   history            — recency-ordered watched ids (engine windows/caps it)
 *   exclude_ids        — unique watched ids (never re-show)
 *   publication_scope  — custom-MCP publishers (?pix_scope on the player's
 *                        own URL), omitted off the MCP surface
 */
export function toChainParams(): ChainParams {
  const params: ChainParams = {
    history: watched.slice(),
    exclude_ids: Array.from(new Set(watched)),
  };
  const scope = readMcpScope();
  if (scope.length) {
    params.publication_scope = scope;
  }
  return params;
}

/** Chain depth = number of watched steps (a chain metric). */
export function depth() {
  return watched.length;
}
